/**
 * UsageMeter — compact circular gauge of checks used vs. the monthly cap, for
 * the sidebar footer. Claude-Code-style ring: a muted track plus a blue arc
 * that fills clockwise as checks are consumed, turning amber near the cap and
 * red at it. A hover tooltip carries the plan and reset date.
 *
 * Self-contained: reads useBillingUsage() and renders nothing until there's a
 * real cap to show (no billing row / unlimited plan → hidden), so it's safe to
 * drop into the sidebar unconditionally.
 */
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { useBillingUsage } from '@/hooks/useBillingUsage'

const PLAN_LABELS: Record<string, string> = {
  start: 'Start',
  team: 'Team',
  enterprise: 'Enterprise',
}

const WARN_AT = 0.8 // amber threshold

function colorFor(pct: number): string {
  if (pct >= 1) return 'var(--destructive)'
  if (pct >= WARN_AT) return 'var(--color-warning)'
  return 'var(--color-pharma-primary)'
}

/** Days-until-reset label, e.g. "Resets in 13 days (Jul 1)". */
function resetText(periodResetAt: string | null | undefined): string | null {
  if (!periodResetAt) return null
  const reset = new Date(periodResetAt)
  if (Number.isNaN(reset.getTime())) return null
  const days = Math.ceil((reset.getTime() - Date.now()) / 86_400_000)
  const date = reset.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (days <= 0) return `Resets ${date}`
  if (days === 1) return `Resets tomorrow (${date})`
  return `Resets in ${days} days (${date})`
}

function Ring({ pct, size = 22, stroke = 3 }: { pct: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const clamped = Math.min(Math.max(pct, 0), 1)
  const center = size / 2
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-hidden="true">
      <circle cx={center} cy={center} r={r} fill="none" strokeWidth={stroke} style={{ stroke: 'var(--border)' }} />
      <circle
        cx={center}
        cy={center}
        r={r}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
        style={{
          stroke: colorFor(pct),
          strokeDasharray: c,
          strokeDashoffset: c * (1 - clamped),
          transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease',
        }}
      />
    </svg>
  )
}

export function UsageMeter({ collapsed }: { collapsed: boolean }) {
  const { usage } = useBillingUsage()

  // Hide entirely unless there's a real cap to gauge against (fail-open orgs,
  // unlimited plans, or no billing row show nothing rather than a broken ring).
  if (!usage || !usage.metered || usage.cap == null || usage.cap <= 0) return null

  const used = usage.checks_used ?? 0
  const cap = usage.cap
  const pct = used / cap
  const remaining = Math.max(cap - used, 0)
  const planLabel = PLAN_LABELS[(usage.plan ?? '').toLowerCase()] ?? usage.plan ?? ''
  const reset = resetText(usage.period_reset_at)
  const ariaLabel = `${used} of ${cap} checks used this period`

  const tooltip = (
    <div className="text-xs leading-relaxed">
      <div className="font-medium">
        {used.toLocaleString()} / {cap.toLocaleString()} checks used
      </div>
      <div className="text-muted-foreground">
        {pct >= 1 ? 'Monthly limit reached' : `${remaining.toLocaleString()} left`}
        {planLabel ? ` · ${planLabel} plan` : ''}
      </div>
      {reset ? <div className="text-muted-foreground">{reset}</div> : null}
    </div>
  )

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 pb-3 pt-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={ariaLabel}
            >
              <Ring pct={pct} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{tooltip}</TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="px-3 pb-3 pt-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="flex w-full items-center gap-2 rounded-md px-1 py-1 hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={ariaLabel}
          >
            <Ring pct={pct} />
            <span className="text-[12px] text-muted-foreground tabular-nums">
              {used.toLocaleString()} / {cap.toLocaleString()}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  )
}
